import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { adminAPI, analyticsAPI, reportsAPI } from "@/services/api";
import { toast } from "sonner";
import { BONUS_TYPES, formatCurrency } from "@/utils/constants";
import { ChevronLeft, ChevronRight, FileDown, Info, RefreshCw, Search } from "lucide-react";
import { canAccessCashPages, hasFullAdminRank } from "@/utils/adminRanks";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from "recharts";

const CATEGORY_COLORS = {
  edition: "#2A4B46",
  parrainage: "#D4A373",
  equipe: "#5A8F7B",
  multiplication: "#8B6F47",
};

const CATEGORY_LABELS = {
  edition: "Bonus-Édition",
  parrainage: "Bonus-Parrainage",
  equipe: "Bonus-Équipe",
  multiplication: "Bonus-Multiplication",
};

const BONUS_EXPLANATIONS = {
  bonus_auteur: "C'est le bonus donne a l'auteur d'une oeuvre de l'esprit. Il represente 50% de la valeur de vente de l'oeuvre.",
  bonus_auteur_1: "Bonus verse au premier ascendant de l'auteur de l'oeuvre. Il represente 4% de la valeur de vente.",
  bonus_auteur_2: "Bonus verse au deuxieme ascendant de l'auteur de l'oeuvre. Il represente 3% de la valeur de vente.",
  bonus_auteur_3: "Bonus verse au troisieme ascendant de l'auteur de l'oeuvre. Il represente 2% de la valeur de vente.",
  etoile_bonus_auteur: "Bonus de 2% verse a l'ascendant Etoile le plus proche de l'auteur.",
  bonus_demarchage_acheteur: "Bonus de 10% verse au demarcheur qui a propose l'ouvrage a l'acheteur.",
  etoile_bonus_demarchage_acheteur: "Bonus de 2% verse a l'ascendant Etoile le plus proche du demarcheur.",
  bonus_filleul_acheteur_1: "Bonus de 5% verse au premier ascendant de l'acheteur.",
  bonus_filleul_acheteur_2: "Bonus de 3% verse au deuxieme ascendant de l'acheteur.",
  bonus_filleul_acheteur_3: "Bonus de 2% verse au troisieme ascendant de l'acheteur.",
  etoile_bonus_filleul_acheteur: "Bonus de 2% verse a l'ascendant Etoile le plus proche de l'acheteur.",
  accompagnement_fondateur_edition: "Accompagnement de 3% reserve aux partenaires-Fondateurs sur les ventes d'oeuvres.",
  bonus_parrainage_1: "Bonus de 50% verse au parrain direct lors de l'ouverture d'un compte-partenaire.",
  bonus_parrainage_2: "Bonus de 10% verse au deuxieme ascendant du nouveau partenaire.",
  bonus_parrainage_3: "Bonus de 5% verse au troisieme ascendant du nouveau partenaire.",
  etoile_bonus_parrainage: "Bonus de 5% verse a l'ascendant Etoile le plus proche du nouveau partenaire.",
  accompagnement_fondateur_parrainage: "Accompagnement de 5% reserve aux partenaires-Fondateurs sur l'ouverture des comptes partenaires.",
  bonus_equipe_p12: "Bonus de 12% verse a l'ascendant P-12 le plus proche du partenaire qui active son compte.",
  bonus_equipe_p24: "Bonus de 14% verse a l'ascendant P-24 le plus proche du partenaire qui active son compte.",
  bonus_equipe_p36: "Bonus de 17% verse a l'ascendant P-36 le plus proche du partenaire qui active son compte.",
  bonus_equipe_e50: "Bonus de 22% verse a l'ascendant Etoile E-50 le plus proche du partenaire qui active son compte.",
  accompagnement_fondateur_equipe: "Accompagnement de 5% reserve aux partenaires-Fondateurs sur les activations de comptes partenaires.",
  bonus_multiplication_e3: "Bonus de 5% verse au premier ascendant Etoile proche de l'ascendant Etoile du partenaire active.",
  bonus_multiplication_e2: "Bonus de 3% verse au deuxieme ascendant Etoile proche de l'ascendant Etoile du partenaire active.",
  bonus_multiplication_e1: "Bonus de 2% verse au troisieme ascendant Etoile proche de l'ascendant Etoile du partenaire active.",
};

const CATEGORY_EXPLANATIONS = {
  "Ã‰dition": "Bonus calcules sur les ventes d'oeuvres pendant la periode.",
  "Édition": "Bonus calcules sur les ventes d'oeuvres pendant la periode.",
  Parrainage: "Bonus calcules sur les frais payes lors de l'ouverture d'un compte-partenaire.",
  "Ã‰quipe": "Bonus calcules sur les frais payes lors de l'activation mensuelle d'un compte-partenaire.",
  "Équipe": "Bonus calcules sur les frais payes lors de l'activation mensuelle d'un compte-partenaire.",
  Multiplication: "Bonus calcules exclusivement pour les ascendants Etoiles lors des activations de comptes partenaires.",
};

function getBonusExplanation(type, info) {
  return BONUS_EXPLANATIONS[type] || CATEGORY_EXPLANATIONS[info?.category] || "Ce bonus suit les règles de distribution definies dans la documentation ARTICO.";
}

const ANALYTICS_COPY = {
  partner: {
    bonusTotal: "Mes bonus attribués",
    operationsValue: "Valeur de mes opérations",
    activationTotal: "Mes activations",
    editionTotal: "Valeur de mes contenus",
    recycled: "Bonus recyclés liés",
    withdrawalReserved: "Mes retraits réservés",
    withdrawalPaid: "Mes retraits payés",
    remaining: "Solde après distributions",
    grandTotal: "Total de mes bonus",
    categoryTitle: "Mes bonus par catégorie",
    typeTitle: "Mes bonus par type",
    categoryLabels: {
      edition: "Mes bonus Édition",
      parrainage: "Mes bonus Parrainage",
      equipe: "Mes bonus Équipe",
      multiplication: "Mes bonus Multiplication",
    },
  },
  founder: {
    bonusTotal: "Mes accompagnements",
    operationsValue: "Valeur de mes opérations liées",
    activationTotal: "Activations liées",
    editionTotal: "Ventes de contenus liées",
    recycled: "Recyclage lié",
    withdrawalReserved: "Retraits réservés",
    withdrawalPaid: "Retraits payés",
    remaining: "Solde après distributions",
    grandTotal: "Total de mes accompagnements",
    categoryTitle: "Mes accompagnements par catégorie",
    typeTitle: "Mes accompagnements par type",
    categoryLabels: {
      edition: "Accompagnement Édition",
      parrainage: "Accompagnement Parrainage",
      equipe: "Accompagnement Équipe",
      multiplication: "Bonus Multiplication",
    },
  },
  cash: {
    bonusTotal: "Total des bonus distribués",
    operationsValue: "Total des encaissements",
    activationTotal: "Encaissements activations",
    editionTotal: "Encaissements contenus",
    recycled: "Bonus recyclés",
    withdrawalReserved: "Retraits réservés",
    withdrawalPaid: "Retraits payés",
    remaining: "Solde de caisse après distributions",
    grandTotal: "Total des bonus enregistrés",
    categoryTitle: "Bonus par catégorie",
    typeTitle: "Bonus par type",
    categoryLabels: CATEGORY_LABELS,
  },
  platform: {
    bonusTotal: "Bonus distribués",
    operationsValue: "Valeur totale de la plateforme",
    activationTotal: "Valeur des activations",
    editionTotal: "Valeur des ventes de contenus",
    recycled: "Bonus recyclés",
    withdrawalReserved: "Retraits réservés",
    withdrawalPaid: "Retraits payés",
    remaining: "Solde plateforme après distributions",
    grandTotal: "Total des bonus",
    categoryTitle: "Répartition par catégorie",
    typeTitle: "Top bonus par type",
    categoryLabels: CATEGORY_LABELS,
  },
  artico_owner: {
    bonusTotal: "Bonus distribués par la plateforme",
    operationsValue: "Recettes ARTICO-MMJ",
    activationTotal: "Recettes des activations",
    editionTotal: "Recettes des contenus",
    recycled: "Bonus recyclés vers ARTICO",
    withdrawalReserved: "Retraits réservés",
    withdrawalPaid: "Retraits payés",
    remaining: "Solde ARTICO après distributions",
    grandTotal: "Total des bonus enregistrés",
    categoryTitle: "Répartition ARTICO par catégorie",
    typeTitle: "Bonus ARTICO par type",
    categoryLabels: CATEGORY_LABELS,
  },
};

function getAnalyticsCopy(scope) {
  if (scope === "partner") return ANALYTICS_COPY.partner;
  if (scope === "founder") return ANALYTICS_COPY.founder;
  if (scope === "cash") return ANALYTICS_COPY.cash;
  if (scope === "artico_owner") return ANALYTICS_COPY.artico_owner;
  return ANALYTICS_COPY.platform;
}

const METRIC_TONES = {
  inflow: "border-l-4 border-l-[#2A4B46] bg-[#F2F8F6]",
  bonus: "border-l-4 border-l-[#D4A373] bg-[#FFF8EF]",
  outflow: "border-l-4 border-l-[#B35C44] bg-[#FFF4F0]",
  reserve: "border-l-4 border-l-[#6B7F4F] bg-[#F5F8EF]",
};

export default function AnalyticsDashboardPage({ user }) {
  const [data, setData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [detailTab, setDetailTab] = useState("bonus_details");
  const [detailSearch, setDetailSearch] = useState("");
  const [detailPage, setDetailPage] = useState(1);
  const [exportingLedger, setExportingLedger] = useState(false);
  const [exportingMonthly, setExportingMonthly] = useState(false);
  const [cashFrequency, setCashFrequency] = useState("monthly");
  const [cashPeriod, setCashPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [exportingCash, setExportingCash] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    loadMonthlyAnalytics(selectedMonth);
  }, [selectedMonth]);

  const loadAnalytics = async () => {
    try {
      const res = await analyticsAPI.getBonuses();
      setData(res.data || {});
    } catch (error) {
      toast.error("Erreur lors du chargement des analytics");
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyAnalytics = async (month) => {
    setMonthlyLoading(true);
    try {
      const res = await analyticsAPI.getMonthly(month);
      setMonthlyData(res.data || {});
      setDetailPage(1);
    } catch (error) {
      toast.error("Erreur lors du chargement du détail mensuel");
      setMonthlyData(null);
    } finally {
      setMonthlyLoading(false);
    }
  };

  if (loading) return <LoadingState label="Chargement des analytics..." />;
  if (!data) return <LoadingState label="Aucune donnée" />;

  const handleExportLedger = async () => {
    setExportingLedger(true);
    try {
      const res = await adminAPI.exportTransactions();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "export_transactions.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ledger généré");
    } catch (error) {
      toast.error("Erreur lors de l'export du ledger");
    } finally {
      setExportingLedger(false);
    }
  };

  const handleExportMonthly = async () => {
    setExportingMonthly(true);
    try {
      const res = await analyticsAPI.exportMonthly(selectedMonth);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `analytics_${monthlyData?.scope?.kind === "global" ? "global" : "partenaire"}_${selectedMonth}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export mensuel généré");
    } catch (error) {
      toast.error("Erreur lors de l'export mensuel");
    } finally {
      setExportingMonthly(false);
    }
  };

  const handleExportCashReport = async () => {
    setExportingCash(true);
    try {
      const res = await adminAPI.exportFinancialReport({ frequency: cashFrequency, period: cashPeriod });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `rapport_financier_${cashFrequency}_${cashPeriod}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rapport financier de la caisse généré");
    } catch (error) {
      toast.error("Erreur lors de l'export du rapport financier");
    } finally {
      setExportingCash(false);
    }
  };

  // Pie chart data
  const byCategory = data.by_category || {};
  const monthly = Array.isArray(data.monthly) ? data.monthly : [];
  const topPartners = Array.isArray(data.top_partners) ? data.top_partners : [];
  const byType = data.by_type || {};
  const analyticsScope = monthlyData?.scope?.kind || data?.scope?.kind || (user?.role === "partner" ? "partner" : "global");
  const analyticsCopy = getAnalyticsCopy(analyticsScope);
  const categoryLabels = analyticsCopy.categoryLabels;

  const pieData = Object.entries(byCategory)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      name: categoryLabels[key],
      value: parseFloat(Number(value || 0).toFixed(2)),
      color: CATEGORY_COLORS[key],
    }));

  // Bar chart data (monthly)
  const barData = monthly.map((m) => ({
    month: m.month,
    [categoryLabels.edition]: parseFloat(Number(m.edition || 0).toFixed(2)),
    [categoryLabels.parrainage]: parseFloat(Number(m.parrainage || 0).toFixed(2)),
    [categoryLabels.equipe]: parseFloat(Number(m.equipe || 0).toFixed(2)),
    [categoryLabels.multiplication]: parseFloat(Number(m.multiplication || 0).toFixed(2)),
  }));

  // Top bonus types
  const topTypes = Object.entries(byType)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);

  const monthlySummary = monthlyData?.summary || {};
  const cashSummary = monthlyData?.cash_summary || {};
  const detailSourceRows = Array.isArray(monthlyData?.[detailTab]) ? monthlyData[detailTab] : [];
  const detailNeedle = detailSearch.trim().toLowerCase();
  const detailRows = detailNeedle
    ? detailSourceRows.filter((row) => JSON.stringify(row).toLowerCase().includes(detailNeedle))
    : detailSourceRows;
  const detailPageSize = 10;
  const detailPageCount = Math.max(1, Math.ceil(detailRows.length / detailPageSize));
  const visibleDetailRows = detailRows.slice((detailPage - 1) * detailPageSize, detailPage * detailPageSize);

  const detailTabs = [
    { id: "bonus_details", label: "Bonus", count: monthlyData?.bonus_details?.length || 0 },
    { id: "operations", label: "Opérations", count: monthlyData?.operations?.length || 0 },
    { id: "ledger", label: "Ledger", count: monthlyData?.ledger?.length || 0 },
    { id: "withdrawals", label: "Retraits", count: monthlyData?.withdrawals?.length || 0 },
    ...(analyticsScope === "cash" ? [{ id: "cash_operations", label: "Journal financier", count: monthlyData?.cash_operations?.length || 0 }] : []),
  ];
  const analyticsTitle = analyticsScope === "founder"
    ? "Analytics - Vue fondateur"
    : analyticsScope === "cash"
      ? "Analytics - Vue caisse"
      : analyticsScope === "artico_owner"
        ? "Analytics - Vue ARTICO"
        : analyticsScope === "global"
          ? "Analytics - Vue plateforme"
        : "Mes bonus - Analyse";

  return (
    <PageShell data-testid="analytics-dashboard">
      <PageHeader
        title={analyticsTitle}
        actions={(hasFullAdminRank(user) || canAccessCashPages(user)) && (
          <Button
            onClick={handleExportLedger}
            disabled={exportingLedger}
            className="w-full bg-[#2A4B46] text-white hover:bg-[#1f3a36] sm:w-auto"
            data-testid="export-ledger-button"
          >
            <FileDown size={16} className="mr-2" />
            {exportingLedger ? "Export..." : "Exporter le Ledger (.xlsx)"}
          </Button>
        )}
      />

      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">Détail mensuel</CardTitle>
            <p className="mt-1 text-sm text-[#5C625F]">Bonus et opérations de la période sélectionnée</p>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="analytics-month" className="text-sm font-medium text-[#5C625F]">Mois</label>
            <input
              id="analytics-month"
              type="month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              className="rounded-lg border border-[#D9D6D0] bg-white px-3 py-2 text-sm text-[#2A4B46] outline-none focus:border-[#2A4B46]"
              data-testid="analytics-month"
            />
            <Button type="button" variant="outline" size="icon" onClick={() => loadMonthlyAnalytics(selectedMonth)} disabled={monthlyLoading} aria-label="Actualiser le détail mensuel">
              <RefreshCw size={16} className={monthlyLoading ? "animate-spin" : ""} />
            </Button>
            <Button type="button" variant="outline" onClick={handleExportMonthly} disabled={monthlyLoading || exportingMonthly} className="gap-2">
              <FileDown size={16} />
              {exportingMonthly ? "Export..." : "Exporter"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {monthlyLoading ? (
            <p className="py-8 text-center text-sm text-[#5C625F]">Chargement du détail mensuel...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <MonthlyMetric label={analyticsCopy.bonusTotal} value={monthlySummary.total_bonus_mc} tone="bonus" />
                <MonthlyMetric label={analyticsCopy.operationsValue} value={monthlySummary.total_collected_mc} tone="inflow" />
                <MonthlyMetric label={analyticsCopy.activationTotal} value={monthlySummary.activation_total_mc} tone="inflow" />
                <MonthlyMetric label={analyticsCopy.editionTotal} value={monthlySummary.edition_total_mc} tone="inflow" />
                <MonthlyMetric label={analyticsCopy.recycled} value={monthlySummary.recycled_bonus_mc} tone="reserve" />
                <MonthlyMetric label={analyticsCopy.withdrawalReserved} value={monthlySummary.withdrawal_reserved_mc} tone="outflow" />
                <MonthlyMetric label={analyticsCopy.withdrawalPaid} value={monthlySummary.withdrawal_paid_mc} tone="outflow" />
                <MonthlyMetric label={analyticsCopy.remaining} value={monthlySummary.remaining_after_bonus_distribution_mc} tone="reserve" />
              </div>

              <div className="flex flex-col gap-3 border-b border-[#E6E4E0] pb-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap gap-2">
                  {detailTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => { setDetailTab(tab.id); setDetailPage(1); }}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${detailTab === tab.id ? "bg-[#2A4B46] text-white" : "bg-[#F3EFE9] text-[#2A4B46] hover:bg-[#E6E4E0]"}`}
                    >
                      {tab.label} <span className="ml-1 text-xs opacity-75">{tab.count}</span>
                    </button>
                  ))}
                </div>
                <div className="relative w-full lg:max-w-xs">
                  <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F8C]" />
                  <input
                    value={detailSearch}
                    onChange={(event) => { setDetailSearch(event.target.value); setDetailPage(1); }}
                    placeholder="Rechercher dans le détail..."
                    className="w-full rounded-lg border border-[#D9D6D0] bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#2A4B46]"
                    aria-label="Rechercher dans le détail mensuel"
                  />
                </div>
              </div>

              <MonthlyDetailTable tab={detailTab} rows={visibleDetailRows} />
              <div className="flex items-center justify-between text-sm text-[#5C625F]">
                <span>{detailRows.length} élément{detailRows.length > 1 ? "s" : ""}</span>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="icon" disabled={detailPage <= 1} onClick={() => setDetailPage((page) => Math.max(1, page - 1))} aria-label="Page précédente">
                    <ChevronLeft size={16} />
                  </Button>
                  <span>Page {Math.min(detailPage, detailPageCount)} / {detailPageCount}</span>
                  <Button type="button" variant="outline" size="icon" disabled={detailPage >= detailPageCount} onClick={() => setDetailPage((page) => Math.min(detailPageCount, page + 1))} aria-label="Page suivante">
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {analyticsScope === "cash" && (
        <>
          <Card className="border-[#E6E4E0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">Rapport financier de la caisse</CardTitle>
              <p className="text-sm text-[#5C625F]">Commandes, activations, paiements, ledger et retraits de la période choisie.</p>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="w-full sm:max-w-[180px]">
                <label htmlFor="cash-report-frequency" className="text-sm font-medium text-[#5C625F]">Fréquence</label>
                <select
                  id="cash-report-frequency"
                  value={cashFrequency}
                  onChange={(event) => setCashFrequency(event.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-[#D9D6D0] bg-white px-3 text-sm text-[#2A4B46] outline-none focus:border-[#2A4B46]"
                >
                  <option value="daily">Journalier</option>
                  <option value="weekly">Hebdomadaire</option>
                  <option value="monthly">Mensuel</option>
                </select>
              </div>
              <div className="w-full sm:max-w-[260px]">
                <label htmlFor="cash-report-period" className="text-sm font-medium text-[#5C625F]">Période</label>
                <input
                  id="cash-report-period"
                  value={cashPeriod}
                  onChange={(event) => setCashPeriod(event.target.value)}
                  placeholder="YYYY-MM / YYYY-MM-DD / YYYY-W22"
                  className="mt-1 h-10 w-full rounded-lg border border-[#D9D6D0] bg-white px-3 text-sm text-[#2A4B46] outline-none focus:border-[#2A4B46]"
                />
              </div>
              <Button type="button" onClick={handleExportCashReport} disabled={exportingCash || !cashPeriod.trim()} className="gap-2 bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
                <FileDown size={16} />
                {exportingCash ? "Génération..." : "Exporter le rapport"}
              </Button>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <CashMetric label="Opérations enregistrées" value={cashSummary.order_count} />
            <CashMetric label="Opérations validées" value={cashSummary.validated_order_count} />
            <CashMetric label="Preuves manquantes" value={cashSummary.missing_payment_proof_count} />
            <CashMetric label="Écritures ledger" value={cashSummary.ledger_count} />
          </div>
        </>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Object.entries(byCategory).map(([cat, amount]) => (
          <Card key={cat} className="border-[#E6E4E0] shadow-sm" style={{ borderLeft: `4px solid ${CATEGORY_COLORS[cat]}` }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-[#5C625F]">{categoryLabels[cat]}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold" style={{ color: CATEGORY_COLORS[cat] }} data-testid={`cat-${cat}`}>
                {formatCurrency(Number(amount || 0))}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Total */}
      <Card className="border-[#E6E4E0] bg-gradient-to-r from-[#2A4B46] to-[#3a6b64] text-white shadow-sm">
        <CardContent className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-white/70">{analyticsCopy.grandTotal}</p>
            <p className="text-3xl font-extrabold sm:text-4xl" data-testid="analytics-total">{formatCurrency(Number(data.total || 0))}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-sm text-white/70">Périodes</p>
            <p className="text-2xl font-bold">{monthly.length}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pie chart */}
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">
              {analyticsCopy.categoryTitle}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `${v.toFixed(2)} MC`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-[#5C625F] py-8">Pas de données</p>
            )}
          </CardContent>
        </Card>

        {/* Top bonus types */}
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">
              {analyticsCopy.typeTitle}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topTypes.length > 0 ? (
              <div className="space-y-3">
                {topTypes.map(([type, amount]) => {
                  const info = BONUS_TYPES[type] || { label: type, rate: "" };
                  const maxVal = topTypes[0][1];
                  const pct = maxVal > 0 ? (amount / maxVal) * 100 : 0;
                  return (
                    <div key={type}>
                      <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                        <BonusInfoPopover title={info.label} rate={info.rate} explanation={getBonusExplanation(type, info)} />
                        <span className="text-[#D4A373] font-bold shrink-0">{formatCurrency(amount)}</span>
                      </div>
                      <div className="h-2 bg-[#F3EFE9] rounded-full overflow-hidden">
                        <div className="h-full bg-[#2A4B46] rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-[#5C625F] py-8">Pas de données</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly bar chart */}
      {barData.length > 0 && (
        <Card className="border-[#E6E4E0] shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">
              Évolution mensuelle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] sm:h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E4E0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${v.toFixed(2)} MC`} />
                <Legend />
                <Bar dataKey={categoryLabels.edition} fill={CATEGORY_COLORS.edition} radius={[4, 4, 0, 0]} />
                <Bar dataKey={categoryLabels.parrainage} fill={CATEGORY_COLORS.parrainage} radius={[4, 4, 0, 0]} />
                <Bar dataKey={categoryLabels.equipe} fill={CATEGORY_COLORS.equipe} radius={[4, 4, 0, 0]} />
                <Bar dataKey={categoryLabels.multiplication} fill={CATEGORY_COLORS.multiplication} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Monthly Reports */}
      {(hasFullAdminRank(user) || canAccessCashPages(user)) && monthly.length > 0 && (
        <Card className="border-[#E6E4E0] shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">
              Exporter les rapports mensuels (Excel)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {monthly.map((m) => (
                <a
                  key={m.month}
                  href={reportsAPI.getMonthlyUrl(m.month)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#F3EFE9] hover:bg-[#E6E4E0] text-[#2A4B46] rounded-lg text-sm font-medium transition-colors"
                  data-testid={`export-report-${m.month}`}
                >
                  <FileDown size={16} />
                  {m.month} ({formatCurrency(m.total)})
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top partners (admin only) */}
      {topPartners.length > 0 && (
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">
              Top 10 partenaires
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 md:hidden">
              {topPartners.map((p, i) => (
                <div key={p.id} className="rounded-2xl border border-[#E6E4E0] bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-[#2A4B46]">{p.name}</p>
                      <p className="font-mono text-xs text-[#5C625F]">{p.id}</p>
                    </div>
                    <span className="font-bold text-[#D4A373]">#{i + 1}</span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                    <span className="text-[#5C625F]">{p.level || "-"}</span>
                    <span className="font-bold text-[#2A4B46]">{formatCurrency(p.total)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E6E4E0]">
                    <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">#</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">Partenaire</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">Niveau</th>
                    <th className="text-right py-3 px-4 font-semibold text-[#2A4B46]">Total bonus</th>
                  </tr>
                </thead>
                <tbody>
                  {topPartners.map((p, i) => (
                    <tr key={p.id} className="border-b border-[#E6E4E0] hover:bg-[#F3EFE9]">
                      <td className="py-3 px-4 font-bold text-[#D4A373]">{i + 1}</td>
                      <td className="py-3 px-4 font-semibold text-[#2A4B46]">{p.name}</td>
                      <td className="py-3 px-4 text-[#5C625F] font-mono text-xs">{p.id}</td>
                      <td className="py-3 px-4 text-[#5C625F]">{p.level || "—"}</td>
                      <td className="py-3 px-4 text-right font-bold text-[#2A4B46]">{formatCurrency(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageShell>
  );
}

function MonthlyMetric({ label, value, tone = "inflow" }) {
  return (
    <div className={`rounded-lg border border-[#E6E4E0] p-3 ${METRIC_TONES[tone] || METRIC_TONES.inflow}`}>
      <p className="text-xs text-[#5C625F]">{label}</p>
      <p className="mt-1 text-lg font-bold text-[#2A4B46]">{formatCurrency(Number(value || 0))}</p>
    </div>
  );
}

function CashMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-[#E6E4E0] bg-[#F9F7F4] p-3">
      <p className="text-xs text-[#5C625F]">{label}</p>
      <p className="mt-1 text-lg font-bold text-[#2A4B46]">{Number(value || 0).toLocaleString("fr-FR")}</p>
    </div>
  );
}

function MonthlyDetailTable({ tab, rows }) {
  if (!rows.length) return <p className="py-8 text-center text-sm text-[#5C625F]">Aucune donnée pour cette période.</p>;

  const cells = (row) => {
    if (tab === "bonus_details") return [
      row.created_at?.slice(0, 10) || "-", row.partner_name || row.partner_id || "-", row.label || row.bonus_type || "-", row.category || "-", formatCurrency(row.amount_mc),
    ];
    if (tab === "operations") return [
      row.created_at?.slice(0, 10) || "-", row.operation_kind === "activation" ? "Activation" : "Édition", row.product_title || row.product_id || "-", row.buyer_name || row.buyer_id || "-", row.status_label || row.status || "-", formatCurrency(row.amount_mc),
    ];
    if (tab === "withdrawals") return [
      row.created_at?.slice(0, 10) || "-", row.partner_name || row.partner_id || "-", row.status_label || row.status || "-", row.selection_mode || "-", formatCurrency(row.reserved_amount_mc),
    ];
    if (tab === "cash_operations") return [
      row.created_at?.slice(0, 10) || "-", row.operation_kind || row.operation_type || "-", row.reference || row.id || "-", row.user_name || row.user_id || "-", row.status_label || row.status || "-", row.payment_method || (row.payment_proof_uploaded ? "Preuve déposée" : "-"), formatCurrency(row.amount_mc),
    ];
    return [
      row.created_at?.slice(0, 10) || "-", row.user_name || row.user_id || "-", row.type_label || row.type || "-", row.bonus_type || "-", formatCurrency(row.amount_mc), row.description || "-",
    ];
  };
  const headers = tab === "bonus_details"
    ? ["Date", "Bénéficiaire", "Bonus", "Catégorie", "Montant"]
    : tab === "operations"
      ? ["Date", "Type", "Produit", "Acheteur", "Statut", "Valeur"]
      : tab === "withdrawals"
        ? ["Date", "Partenaire", "Statut", "Sélection", "Montant"]
          : tab === "cash_operations"
            ? ["Date", "Opération", "Référence", "Utilisateur", "Statut", "Paiement", "Montant"]
            : ["Date", "Utilisateur", "Mouvement", "Bonus", "Montant", "Description"];

  return (
    <div className="overflow-x-auto rounded-lg border border-[#E6E4E0]">
      <table className="w-full min-w-[700px] text-sm">
        <thead className="bg-[#F3EFE9]">
          <tr>{headers.map((header) => <th key={header} className="whitespace-nowrap px-3 py-3 text-left font-semibold text-[#2A4B46]">{header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || `${tab}-${index}`} className="border-t border-[#E6E4E0] hover:bg-[#FCFBF9]">
              {cells(row).map((cell, cellIndex) => <td key={`${row.id || index}-${cellIndex}`} className="max-w-[280px] truncate px-3 py-3 text-[#5C625F]">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BonusInfoPopover({ title, rate, explanation }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex min-w-0 items-center gap-2 rounded-full pr-2 text-left font-medium text-[#2A4B46] transition-colors hover:bg-[#F3EFE9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
          aria-label={`Comprendre ${title}`}
        >
          <span className="truncate">{title}</span>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#5C625F]">
            <Info size={14} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl border-[#E6E4E0] bg-white p-4 shadow-lg">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <p className="font-semibold text-[#2A4B46]">{title}</p>
            {rate && <span className="rounded-full bg-[#F7E9D8] px-2 py-1 text-xs font-bold text-[#A86F3C]">{rate}</span>}
          </div>
          <p className="text-sm leading-6 text-[#5C625F]">{explanation}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
